import { Inject, Injectable } from '@angular/core';
import { Rejection, StateObject, Transition } from '@uirouter/angular';
import { IExtendedWindow, IObject } from '@type_definitions/datatypes';
import { util } from './shared/util';
import { STATE_ACCESS, STATE_LOGIN, STATE_MAIL, STATE_RESET_PASSWORD, STATE_USER_INFO, STATE_USER_PROFILE } from './shared/app-states';
import { AuthService } from './shared/services/auth/auth.service';
import * as _ from 'underscore';
import { AppStatesService } from './app-states.service';
import { StateDeclaration } from '@uirouter/core';
import { TitleService } from './shared/services/title.service';
import { BodyClassService } from './shared/services/body-class.service';
import { GaMessageService } from './shared/message/ga-message.service';
import { ScrollService } from './shared/scroll/scroll.service';
import { DEFAULT_LANGUAGE } from './shared/language/language-data.service';
import { LanguageService } from './shared/language/language.service';
import { TranslateService } from '@ngx-translate/core';
import { TranslationHelperService } from './shared/translation/translation-helper.service';
import { TranslationCategory } from './shared/services/ga-state.service';

export interface IUnprotectedState {
  path:RegExp;
  noAutoAuthentication?:boolean;
  noStateChangeAfterAuthentication?:boolean;
}

export const UNPROTECTED_STATES_PATHS:IUnprotectedState[] = [
  {
    path: /\/login/,
    noAutoAuthentication: true
  },
  {
    path: /\/forgot-password/,
    noAutoAuthentication: true
  },
  {
    path: /\/reset-password/,
    noAutoAuthentication: true
  },
  {
    path: /\/signup\/.*/,
    noAutoAuthentication: true
  },
  {
    path: /\/mail\/unsubscribe/,
    noAutoAuthentication: true
  },
  {
    path: /\/createticket\/.*/,
    noStateChangeAfterAuthentication: true
  }
];

@Injectable()
export class AppTransitionService {

  constructor(
    private authService:AuthService,
    private appStatesService:AppStatesService,
    private titleService:TitleService,
    private bodyClassService:BodyClassService,
    private messageService:GaMessageService,
    private scrollService:ScrollService,
    private languageService:LanguageService,
    private translateService:TranslateService,
    private translationHelperService:TranslationHelperService,
    @Inject(Window) private window:IExtendedWindow
  ) {
    /**/
  }

  public get unprotectedUrl():IUnprotectedState {
    let currentUrlPath:string = this.window.location.hash.substr(1, this.window.location.hash.length);
    if (currentUrlPath.includes('?')) {
      currentUrlPath = currentUrlPath.split('?')[0];
    }
    return UNPROTECTED_STATES_PATHS.find(unprotectedPath => unprotectedPath.path.test(currentUrlPath));
  }

  public onTransitionBefore(transition:Transition):void {
    const isUITranslatable:Function = ():boolean => {
      const to:StateObject = transition.$to();
      const translate:string = to.data && to.data.translate; // this navigates from child -> parent -> grandparent -> (...) and stops when finds the 'translation' defined
      const languages:string = to.data && to.data.languages; // this navigates from child -> parent -> grandparent -> (...) and stops when finds the 'languages' defined
      return translate && _.isEmpty(languages) || _.contains(languages, this.languageService.getUserLanguage() || DEFAULT_LANGUAGE);
    };

    const uiLanguage:string = isUITranslatable()? this.languageService.determineLanguage() : DEFAULT_LANGUAGE;
    this.languageService.uiLanguage = uiLanguage;
    this.translateService.use(uiLanguage);
    this.translationHelperService.loadTranslations({ modules: [TranslationCategory.GA, TranslationCategory.APP] }, true);
    console.debug(`UI language: ${uiLanguage}`);
  }

  public onTransitionStart(transition:Transition):void {
    const fromState:StateDeclaration = transition.from();
    const fromParams:IObject = transition.params('from');
    const toState:StateDeclaration = transition.to();
    const toParams:IObject = transition.params('to');

    console.debug('stateChangeStart: ' + this.stateLog(fromState, fromParams, toState, toParams));

    // capture view data at the start
    this.authService.captureViewData(transition);

    // we got a token in the url -> start auto authentication
    // this scenario only happens when receive different token from previous one (e.g. reset password)
    if (toParams.token && toParams.token !== (fromParams.token || this.authService.getToken()) && !this.authService.isAuthenticating()) {
      console.debug('We got a token, start auto authentication');

      const stateParams:IObject = util.copyAndExtend(toParams);
      if (toState.name !== STATE_RESET_PASSWORD) {
        //do not keep the token and acslToken, unless it's for /reset-password
        stateParams.token = undefined;
        stateParams.acslToken = undefined;
      }
      this.authService.setStateAfterLogin({
        stateName: toState.name,
        stateParams
      });

      this.authService.activate();
      transition.abort();
      return;
    }

    if (!this.unprotectedState(toState) && !this.authService.isAuthenticated()) {
      this.authService.setStateAfterLogin({
        stateName: toState.name,
        stateParams: toParams
      });
      console.debug('Not authenticated: preventing state change');
      transition.abort();
      if (!this.authService.isAuthenticating()) {
        if (!fromState || fromState.name !== STATE_LOGIN) {
          this.stateTo(STATE_LOGIN, undefined, transition);
        } else {
          // abort the transition because we want to keep user in current page
          transition.abort();
        }
      }
      return;
    }

    if (this.authService.getCurrentUser()?.registrationState === AuthService.STATE_APPROVED) {
      if (toState && toState.name.indexOf(STATE_USER_PROFILE) !== 0) {
        console.debug('Not authorized: preventing state change');
        transition.abort();
        this.stateTo(STATE_USER_INFO, { frameless: true }, transition);
      }
      return;
    }

    _.forEach(this.appStatesService.clientRedirects, (tryRedirect:Function) => {
      tryRedirect(transition);
    });
  }

  public onTransitionSuccess(transition:Transition):void {
    const fromState:StateDeclaration = transition.from();
    const fromParams:IObject = transition.params('from');
    const toState:StateDeclaration = transition.to();
    const toParams:IObject = transition.params('to');

    console.debug('stateChangeSuccess: ' + this.stateLog(fromState, fromParams, toState, toParams));
    this.changeTitleForState(toState);
    this.setBodyClass(toState, fromState);
    this.scrollToContentTop(toState);
    this.closeOpenDialogs();
  }

  public onTransitionError(transition:Transition):void {
    const fromState:StateDeclaration = transition.from();
    const fromParams:IObject = transition.params('from');
    const toState:StateDeclaration = transition.to();
    const toParams:IObject = transition.params('to');
    const transitionRejection:Rejection = transition.error();

    // if transition is ignored or superseded do not log into console
    // https://ui-router.github.io/ng2/docs/2.0.2/enums/transition.rejecttype.html
    if (!transitionRejection.message?.includes('ignored') && !transitionRejection.message?.includes('superseded')) {
      console.error('stateChangeError: ' + this.stateLog(fromState, fromParams, toState, toParams) + ': ' + transitionRejection.message);
    }
  }

  private changeTitleForState(toState:StateDeclaration):void {
    this.titleService.setTitle((toState.data?.viewTitle) || '');
  }

  private setBodyClass(toState:StateDeclaration, fromState:StateDeclaration):void {
    if (fromState?.data?.bodyClass) {
      this.bodyClassService.remove(fromState.data.bodyClass);
    }
    if (toState?.data?.bodyClass) {
      this.bodyClassService.add(toState.data.bodyClass);
    }
  }

  private scrollToContentTop(toState:StateDeclaration):void {
    if (!toState?.data?.noScrollTop) {
      this.scrollService.scrollTo('content-top');
    }
  }

  private closeOpenDialogs():void {
    this.messageService.closeAllDialogs();
  }

  private unprotectedState(state:StateDeclaration):boolean {
    // check if the parent of the current state is unprotected
    return state && (state.name.indexOf(STATE_ACCESS) === 0 || state.name.indexOf(STATE_MAIL) === 0);
  }

  private stateTo(stateName:string, toParams?:IObject, transition?:Transition):void {
    transition.router.stateService.transitionTo(stateName, toParams, {
      location: true,
      inherit: true,
      relative: transition.router.globals.current
    });
  }

  private stateLog(fromState:StateDeclaration, fromParams:IObject, toState:StateDeclaration, toParams:IObject):string {
    const fromStateName:string = fromState? fromState.name : '';
    const fromStateParams:string = (util.notNullOrUndefinedOrEmpty(fromParams)? ` (${util.toJson(fromParams)})` : '')
      .replace(/"token":"\w+"/, '"token":"<token>"');

    const toStateName:string = toState? toState.name : '';
    const toStateParams:string = (util.notNullOrUndefinedOrEmpty(toParams)? ` (${util.toJson(toParams)})` : '')
      .replace(/"token":"\w+"/, '"token":"<token>"');

    return `${fromStateName}${fromStateParams} -> ${toStateName}${toStateParams}`;
  }
}